/* ==========================================================================
   Proadsy Storytelling — Cinematic scroll-triggered narrative
   v1.0 — 2026-05-04
   --------------------------------------------------------------------------
   4 modules:
     1) initHeroParallax()       — hero parallax + entry stagger timeline
     2) initStatsCountUp()       — count-up animation for big numbers
     3) initFabricLabReveal()    — sequential 4-layer reveal + glow pulse
     4) initScenarioReveal()     — clip-path mask reveal + parallax for scenes
   --------------------------------------------------------------------------
   Uses: GSAP 3.12 + ScrollTrigger plugin (loaded by theme.liquid)
   Respects prefers-reduced-motion. No scroll-jacking. Targets only sections
   that opted-in via [data-storytelling="..."] attributes.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- Reduced-motion bypass ---- */
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  /* ---- Apple-grade easing ---- */
  var EASE_SPRING = 'cubic-bezier(0.16, 1, 0.3, 1)'; // CSS string
  var EASE_GSAP = 'power3.out';                       // gsap-readable

  /* ---- Reveal failsafe ----
     Any element hidden by a reveal module MUST end up visible no matter what.
     Dual trigger: ScrollTrigger (animation timing) + IntersectionObserver
     (immune to layout shifts / refresh timing), whichever fires first.
     Plus an unconditional timer reveal so content can never stay blank
     (no-scroll renderers, missed triggers, late GSAP boot). */
  var FAILSAFE_MS = 5000;
  var _pendingReveals = [];
  function armFailsafe() {
    setTimeout(function () {
      _pendingReveals.forEach(function (fire) { fire(); });
      _pendingReveals = [];
    }, FAILSAFE_MS);
  }
  /* Register a one-shot reveal: el enters viewport (ST or IO) → fire(). */
  function onceVisible(el, startPct, fire) {
    var done = false;
    var io = null;
    var wrapped = function () {
      if (done) return;
      done = true;
      if (io) { io.disconnect(); io = null; }
      fire();
    };
    ScrollTrigger.create({
      trigger: el,
      start: 'top ' + startPct + '%',
      once: true,
      onEnter: wrapped
    });
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) wrapped(); });
      }, { rootMargin: '0px 0px ' + (startPct - 100) + '% 0px', threshold: 0.01 });
      io.observe(el);
    }
    _pendingReveals.push(wrapped);
  }

  /* ---- Wait for GSAP + ScrollTrigger (defer ordering safety) ---- */
  var _retries = 0;
  function boot() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      _retries++;
      if (_retries < 40) {
        setTimeout(boot, 100);
        return;
      }
      // GSAP missing entirely — fail silently; pre-set inline opacity already 1 so no FOUC.
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    initHeroParallax();
    initStatsCountUp();
    initFabricLabReveal();
    initScenarioReveal();
    armFailsafe();
    // Re-poke ScrollTrigger after possible late-loaded images shift layout
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ==========================================================================
     1. HERO — parallax + entry timeline
     - Content drifts UP slower than scroll (factor ~0.5)
     - Background drifts DOWN inverse (factor ~1.3) — depth illusion
     - Entry: badge → h1 → sub → CTA → tool, staggered ~150ms
     ========================================================================== */
  function initHeroParallax() {
    var hero = document.querySelector('[data-storytelling="hero"]');
    if (!hero) return;

    // --- Entry timeline (above the fold, no scroll trigger) ---
    // Avoid stomping on existing proadsy-animations.js entry (which already animates .hero-badge / .hero h1 / .hero-sub / .hero-visual).
    // We only ADD a tiny bg-warmup on load and rely on existing entry for content.
    // To stay non-conflicting: only run parallax (scroll-bound), NOT a duplicate entry.

    // --- Scroll parallax: content slow-up, bg slow-down ---
    var content = hero.querySelector('.hero__container');
    var bg = hero.querySelector('.hero__bg');

    if (content) {
      gsap.to(content, {
        yPercent: -8,          // subtle upward drift (factor ~0.5x scroll)
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.5
        }
      });
    }

    if (bg) {
      gsap.to(bg, {
        yPercent: 14,          // background falls slower (factor ~1.3x perceived depth)
        scale: 1.06,           // gentle Ken-Burns growth
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.5
        }
      });
    }
  }

  /* ==========================================================================
     2. STATS — count-up
     - Each [data-storytelling="counter"] gets a one-shot count-up
     - data-target overrides the parsed number; data-suffix preserves "+", "°F" etc
     - Decimals supported via data-decimals="2"
     ========================================================================== */
  function initStatsCountUp() {
    var counters = document.querySelectorAll('[data-storytelling="counter"]');
    if (!counters.length) return;

    counters.forEach(function (el) {
      // Parse target
      var raw = (el.getAttribute('data-target') || el.textContent || '').trim();
      // Preserve any suffix already in the DOM (e.g. "+", "°F", "K")
      var preset = el.getAttribute('data-suffix');
      var suffixMatch = raw.match(/[^\d.,\-]+$/);
      var suffix = preset !== null ? preset : (suffixMatch ? suffixMatch[0] : '');
      var numericRaw = raw.replace(/[^\d.\-]/g, '');
      var target = parseFloat(numericRaw);
      if (isNaN(target)) return;

      var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      if (!decimals && /\./.test(numericRaw)) {
        // auto-detect decimals from raw string
        decimals = (numericRaw.split('.')[1] || '').length;
        if (decimals > 2) decimals = 2;
      }

      // Set start state to 0 so animation has runway
      var formatter = function (v) {
        var n = decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString('en-US');
        return n + suffix;
      };

      // Hold initial paint as 0 only when in viewport — avoid SSR layout shift
      var obj = { val: 0 };

      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          el.textContent = formatter(0);
          gsap.to(obj, {
            val: target,
            duration: 1.4,
            ease: EASE_GSAP,
            onUpdate: function () { el.textContent = formatter(obj.val); },
            onComplete: function () { el.textContent = formatter(target); }
          });
        }
      });
    });
  }

  /* ==========================================================================
     3. FABRIC LAB — layer-by-layer reveal + final glow pulse
     - Each [data-storytelling="layer"] enters from alternating sides + scale 0.95→1
     - 200ms stagger
     - After last layer, brief glow pulse on assembled stack
     ========================================================================== */
  function initFabricLabReveal() {
    var lab = document.querySelector('[data-storytelling="fabric-lab"]');
    if (!lab) return;

    var layers = lab.querySelectorAll('[data-storytelling="layer"]');
    if (!layers.length) return;

    // Pre-set hidden state to prevent FOUC
    gsap.set(layers, { opacity: 0, scale: 0.95 });
    layers.forEach(function (layer, i) {
      gsap.set(layer, { x: i % 2 === 0 ? -40 : 40 });
    });

    onceVisible(lab, 75, function () {
        gsap.to(layers, {
          opacity: 1,
          scale: 1,
          x: 0,
          duration: 0.7,
          stagger: 0.18,
          ease: EASE_GSAP,
          onComplete: function () {
            // Glow pulse on the spec sheet container
            var stack = lab.querySelector('.pf-lab__specs') || lab;
            if (!stack) return;
            var origShadow = stack.style.boxShadow;
            gsap.fromTo(stack,
              { boxShadow: '0 0 0px 0px rgba(37,99,235,0)' },
              {
                boxShadow: '0 0 32px 4px rgba(37,99,235,0.45)',
                duration: 0.55,
                ease: 'power2.out',
                yoyo: true,
                repeat: 1,
                onComplete: function () { stack.style.boxShadow = origShadow || ''; }
              }
            );
          }
        });
    });
  }

  /* ==========================================================================
     4. SCENARIO PROOF — clip-path mask reveal + parallax
     - Each [data-storytelling="scene"] reveals via inset clip from center outward
     - Subtle yPercent parallax on scroll (factor ~0.4)
     ========================================================================== */
  function initScenarioReveal() {
    var section = document.querySelector('[data-storytelling="scenario-proof"]');
    if (!section) return;

    var scenes = section.querySelectorAll('[data-storytelling="scene"]');
    if (!scenes.length) return;

    scenes.forEach(function (scene, i) {
      // Pre-set: hidden via clip-path (collapsed at center)
      gsap.set(scene, {
        clipPath: 'inset(50% 50% 50% 50%)',
        webkitClipPath: 'inset(50% 50% 50% 50%)',
        opacity: 0
      });

      onceVisible(scene, 82, function () {
        gsap.to(scene, {
          clipPath: 'inset(0% 0% 0% 0%)',
          webkitClipPath: 'inset(0% 0% 0% 0%)',
          opacity: 1,
          duration: 0.95,
          delay: i * 0.12,
          ease: EASE_GSAP
        });
      });

      // Subtle parallax: image inside scene drifts up slightly slower than scroll
      var img = scene.querySelector('img');
      if (img) {
        gsap.to(img, {
          yPercent: -6,
          ease: 'none',
          scrollTrigger: {
            trigger: scene,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6
          }
        });
      }
    });
  }
})();
